import React, {useEffect, useRef, useState} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {ProductsCatalog} from './ProductsCatalog'
import {OrderSummary} from './OrderSummary'
import {PageWrapper} from './ProposalBuilder.styles'
import {useProposalCatalog} from './hooks/useProposalCatalog'
import {useProposalPaymentPreview} from './hooks/useProposalPaymentPreview'
import {ProposalPreview} from '../../components/ProposalPreview'
import {
  selectProposalInfo,
  selectIsDirty,
  resetAll,
} from '../../../../app-store/ProposalSlice'
import {DiscardChangesModal} from '../../components/DiscardChangesModal'

export function ProposalBuilder() {
  const {loading, error} = useProposalCatalog()
  const [showPreview, setShowPreview] = useState(false)
  const [isCatalogLocked, setIsCatalogLocked] = useState(false)
  const {proposalId, thryvId, salesRepId} = useSelector(selectProposalInfo)
  const isDirty = useSelector(selectIsDirty)
  const {refreshPaymentPreview} = useProposalPaymentPreview()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false)

  // Guard: if Redux state was cleared (page refresh / direct URL), salesRepId
  // is null — the page can't function without it. Redirect immediately.
  useEffect(() => {
    if (!salesRepId) {
      toast.info('Your session has expired. Please start a new proposal.')
      navigate('/proposal-tool/dashboard', {replace: true})
    }
  }, [salesRepId, navigate])

  // Refresh live due-today pricing once on load when opening an existing
  // proposal for an existing account, same mechanism the Preview/Save/Send
  // actions use (see useProposalPaymentPreview) — a ref guard keeps this to a
  // single run since refreshPaymentPreview's identity changes every time it
  // dispatches the resulting update into Redux.
  const hasRefreshedOnLoad = useRef(false)
  useEffect(() => {
    if (loading || hasRefreshedOnLoad.current) return
    if (!proposalId || !thryvId) return
    hasRefreshedOnLoad.current = true
    refreshPaymentPreview()
  }, [loading, proposalId, thryvId, refreshPaymentPreview])

  // Warn before closing the tab / reloading while there are unsaved
  // proposal option changes.
  useEffect(() => {
    if (!isDirty) return
    const handleBeforeUnload = e => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const handleConfirmLeave = () => {
    setShowLeaveConfirm(false)
    dispatch(resetAll())
    navigate('/proposal-tool/dashboard')
  }

  const handleCancelLeave = () => {
    setShowLeaveConfirm(false)
  }

  const handleBack = () => {
    if (isDirty) {
      setShowLeaveConfirm(true)
      return
    }
    dispatch(resetAll())
    navigate('/proposal-tool/dashboard')
  }

  if (loading) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Unable to load catalog.</div>
  }

  return (
    <PageWrapper>
      {showPreview && (
        <ProposalPreview overlay onBack={() => setShowPreview(false)} />
      )}
      <ProductsCatalog
        onPreview={() => setShowPreview(true)}
        onBack={handleBack}
        isLocked={isCatalogLocked}
      />
      <OrderSummary onPreviewLoadingChange={setIsCatalogLocked} />

      <DiscardChangesModal
        isOpen={showLeaveConfirm}
        onCancel={handleCancelLeave}
        onConfirm={handleConfirmLeave}
      />
    </PageWrapper>
  )
}
